import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { Document, Page } from "react-pdf";
import useMeasure from "react-use-measure";
import { pdfDocumentOptions } from "utils/pdfWorkerSetup";

const PRELOAD_BEFORE = 2;
const PRELOAD_AFTER = 2;
const PAGE_GAP = 16;
/** Treat as "fits in viewport" when no meaningful vertical scroll (subpixel / borders). */
const NO_SCROLL_EPSILON_PX = 2;
const DEFAULT_SCROLL_END_OPTIONS = {
  once: true,
};

function isNoScrollRoot(el) {
  if (!el) return false;
  return el.scrollHeight <= el.clientHeight + NO_SCROLL_EPSILON_PX;
}

function LazyLoadPdfViewer({
  file,
  scale = 1,
  onDocumentLoaded,
  onInitialPagesLoaded,
  onReachEnd,
  renderBottom,
  scrollEndOptions = DEFAULT_SCROLL_END_OPTIONS,
}) {
  const mergedScrollEndOptions = {
    ...DEFAULT_SCROLL_END_OPTIONS,
    ...scrollEndOptions,
  };

  const [containerRef, containerBounds] = useMeasure();
  const [bottomMeasureRef, bottomBounds] = useMeasure();

  const scrollRef = useRef(null);
  const endSentinelRef = useRef(null);
  const loadedPdfRef = useRef(null);
  const heightsGenerationRef = useRef(0);
  const prevContainerWidthRef = useRef(0);

  const hasReachedEndRef = useRef(false);
  const hasUserScrolledRef = useRef(false);

  const [numPages, setNumPages] = useState(0);
  const [pageHeights, setPageHeights] = useState([]);
  const [visibleRange, setVisibleRange] = useState({ start: 1, end: 0 });
  const [initialWindowRendered, setInitialWindowRendered] = useState(false);
  const [isEndDetectionEnabled, setIsEndDetectionEnabled] = useState(false);

  const containerWidth = containerBounds.width || 0;
  const bottomHeight = bottomBounds.height || 0;

  const prevFileRef = useRef(undefined);

  const resetState = useCallback(() => {
    heightsGenerationRef.current += 1;
    prevContainerWidthRef.current = 0;
    loadedPdfRef.current = null;
    setNumPages(0);
    setPageHeights([]);
    setVisibleRange({ start: 1, end: 0 });
    setInitialWindowRendered(false);
    setIsEndDetectionEnabled(false);
    hasReachedEndRef.current = false;
    hasUserScrolledRef.current = false;
  }, []);

  useEffect(() => {
    if (prevFileRef.current === undefined) {
      prevFileRef.current = file;
      return;
    }

    if (prevFileRef.current === file) return;

    prevFileRef.current = file;
    resetState();
  }, [file, resetState]);

  const syncPageHeightFromCanvas = useCallback((pageNumber) => {
    const slot = scrollRef.current?.querySelector(`[data-page-slot="${pageNumber}"]`);
    const canvas = slot?.querySelector("canvas");
    const measuredHeight = canvas?.getBoundingClientRect().height ?? 0;

    if (measuredHeight <= 0) return;

    setPageHeights((prev) => {
      const index = pageNumber - 1;
      const targetLength = loadedPdfRef.current?.numPages ?? Math.max(prev.length, pageNumber);
      const current = prev[index] ?? 0;

      if (Math.abs(current - measuredHeight) < 1) return prev;

      const next = Array.from({ length: targetLength }, (_, i) => prev[i] ?? 0);
      next[index] = measuredHeight;
      return next;
    });
  }, []);

  const calculatePageHeights = useCallback(async (loadedPdf, width) => {
    if (!loadedPdf || !width) return;

    const generation = heightsGenerationRef.current;

    const heights = [];
    for (let pageNumber = 1; pageNumber <= loadedPdf.numPages; pageNumber += 1) {
      const page = await loadedPdf.getPage(pageNumber);
      const viewport = page.getViewport({ scale: 1 });
      const fitScale = width / viewport.width;
      heights.push(viewport.height * fitScale * scale);
    }

    if (generation !== heightsGenerationRef.current) return;

    setPageHeights(heights);
  }, [scale]);

  const onDocumentLoadSuccess = useCallback(
    async (loadedPdf) => {
      loadedPdfRef.current = loadedPdf;
      setNumPages(loadedPdf.numPages);
      setInitialWindowRendered(false);
      setIsEndDetectionEnabled(false);
      hasReachedEndRef.current = false;
      hasUserScrolledRef.current = false;

      if (onDocumentLoaded) {
        onDocumentLoaded();
      }

      if (containerWidth > 0) {
        await calculatePageHeights(loadedPdf, containerWidth);
      }
    },
    [containerWidth, calculatePageHeights, onDocumentLoaded]
  );

  useEffect(() => {
    if (!loadedPdfRef.current || containerWidth <= 0 || numPages <= 0) return undefined;
    const heightsReady = pageHeights.length === numPages && pageHeights.every((h) => h > 0);
    if (heightsReady) return undefined;

    let cancelled = false;

    (async () => {
      await calculatePageHeights(loadedPdfRef.current, containerWidth);
      if (cancelled) return;
    })();

    return () => {
      cancelled = true;
    };
  }, [containerWidth, numPages, pageHeights.length, calculatePageHeights]);

  useEffect(() => {
    if (!loadedPdfRef.current || containerWidth <= 0) return undefined;
    if (prevContainerWidthRef.current === containerWidth) return undefined;

    prevContainerWidthRef.current = containerWidth;

    let cancelled = false;

    (async () => {
      await calculatePageHeights(loadedPdfRef.current, containerWidth);
      if (cancelled) return;
    })();

    return () => {
      cancelled = true;
    };
  }, [containerWidth, calculatePageHeights]);

  const pagesHeight = useMemo(() => {
    if (!pageHeights.length) return 0;
    return pageHeights.reduce((sum, h) => sum + h, 0) + PAGE_GAP * (pageHeights.length - 1);
  }, [pageHeights]);

  const offsets = useMemo(() => {
    const result = [];
    let top = 0;

    for (let i = 0; i < pageHeights.length; i += 1) {
      result.push(top);
      top += pageHeights[i] + PAGE_GAP;
    }

    return result;
  }, [pageHeights]);

  const bottomTop = pagesHeight > 0 ? pagesHeight : 0;
  const sentinelTop = bottomTop + bottomHeight;
  const totalHeight = sentinelTop + 1;

  const updateVisibleRange = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !pageHeights.length || !numPages) return;

    const scrollTop = el.scrollTop;
    const viewportBottom = scrollTop + el.clientHeight;

    let firstVisible = 1;
    let lastVisible = numPages;

    for (let i = 0; i < numPages; i += 1) {
      const pageTop = offsets[i];
      const pageBottom = pageTop + pageHeights[i];

      if (pageBottom >= scrollTop) {
        firstVisible = i + 1;
        break;
      }
    }

    for (let i = firstVisible - 1; i < numPages; i += 1) {
      const pageTop = offsets[i];
      if (pageTop > viewportBottom) {
        lastVisible = i;
        break;
      }
    }

    const start = Math.max(1, firstVisible - PRELOAD_BEFORE);
    const end = Math.min(numPages, lastVisible + PRELOAD_AFTER);

    setVisibleRange((prev) => {
      if (prev.start === start && prev.end === end) {
        return prev;
      }
      return { start, end };
    });
  }, [numPages, offsets, pageHeights]);

  const handleScroll = useCallback(() => {
    hasUserScrolledRef.current = true;
    updateVisibleRange();
  }, [updateVisibleRange]);

  useEffect(() => {
    updateVisibleRange();
  }, [updateVisibleRange, pagesHeight, totalHeight]);

  useEffect(() => {
    const isReady =
      numPages > 0 &&
      pageHeights.length === numPages &&
      initialWindowRendered &&
      pagesHeight > 0;

    setIsEndDetectionEnabled(isReady);
  }, [numPages, pageHeights.length, initialWindowRendered, pagesHeight]);

  const tryFireReachEnd = useCallback(() => {
    if (!onReachEnd) return;
    if (mergedScrollEndOptions.once && hasReachedEndRef.current) return;
    hasReachedEndRef.current = true;
    onReachEnd();
  }, [onReachEnd, mergedScrollEndOptions.once]);

  useLayoutEffect(() => {
    if (!isEndDetectionEnabled || !onReachEnd || !mergedScrollEndOptions.once) return;
    const root = scrollRef.current;
    if (!root || !isNoScrollRoot(root)) return;
    tryFireReachEnd();
  }, [
    isEndDetectionEnabled,
    mergedScrollEndOptions.once,
    onReachEnd,
    tryFireReachEnd,
    sentinelTop,
    bottomHeight,
    totalHeight,
  ]);

  useEffect(() => {
    const root = scrollRef.current;
    const target = endSentinelRef.current;

    if (!root || !target || !onReachEnd || !isEndDetectionEnabled) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          if (!mergedScrollEndOptions.once) {
            hasReachedEndRef.current = false;
          }
          return;
        }

        const allowWithoutUserScroll = isNoScrollRoot(root);

        if (!hasUserScrolledRef.current && !allowWithoutUserScroll) {
          return;
        }

        tryFireReachEnd();
      },
      {
        root,
        rootMargin: "0px 0px 20px 0px",
        threshold: 0,
      }
    );

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, [onReachEnd, isEndDetectionEnabled, mergedScrollEndOptions.once, sentinelTop, tryFireReachEnd]);

  const handlePageRenderSuccess = useCallback(
    (page) => {
      syncPageHeightFromCanvas(page?.pageNumber ?? 0);

      if (!initialWindowRendered && visibleRange.end > 0) {
        setInitialWindowRendered(true);

        if (onInitialPagesLoaded) {
          onInitialPagesLoaded();
        }
      }
    },
    [initialWindowRendered, visibleRange.end, onInitialPagesLoaded, syncPageHeightFromCanvas]
  );

  if (!file) return null;

  const documentKey = file.name ? `${file.name}-${file.size}-${file.lastModified}` : "pdf";

  return (
    <ScrollContainer ref={scrollRef} onScroll={handleScroll}>
      <MeasuredWidthContainer ref={containerRef}>
        {containerWidth > 0 ? (
          <Document
            key={documentKey}
            file={file}
            onLoadSuccess={onDocumentLoadSuccess}
            options={pdfDocumentOptions}
          >
            <PagesHost style={{ height: totalHeight || "auto" }}>
              {Array.from({ length: numPages }, (_, index) => {
                const pageNumber = index + 1;
                const top = offsets[index] || 0;
                const height = pageHeights[index] || 0;
                const shouldRender =
                  pageNumber >= visibleRange.start && pageNumber <= visibleRange.end;

                return (
                  <PageSlot
                    key={pageNumber}
                    data-page-slot={pageNumber}
                    style={{
                      top,
                      height,
                    }}
                  >
                    {shouldRender ? (
                      <Page
                        pageNumber={pageNumber}
                        width={containerWidth}
                        scale={scale}
                        renderMode="canvas"
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        devicePixelRatio={Math.min(2, window.devicePixelRatio || 1)}
                        onRenderSuccess={handlePageRenderSuccess}
                      />
                    ) : null}
                  </PageSlot>
                );
              })}

              {renderBottom ? (
                <BottomSlot ref={bottomMeasureRef} style={{ top: bottomTop }}>
                  {typeof renderBottom === "function" ? renderBottom() : renderBottom}
                </BottomSlot>
              ) : (
                <BottomMeasureStub ref={bottomMeasureRef} style={{ top: bottomTop }} />
              )}

              <EndSentinel ref={endSentinelRef} style={{ top: sentinelTop }} />
            </PagesHost>
          </Document>
        ) : null}
      </MeasuredWidthContainer>
    </ScrollContainer>
  );
}

export default LazyLoadPdfViewer;

const ScrollContainer = styled.div`
  overflow: auto;
  height: 100%;
`;

const MeasuredWidthContainer = styled.div`
  width: 100%;
`;

const PagesHost = styled.div`
  position: relative;
  width: 100%;
`;

const PageSlot = styled.div`
  position: absolute;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: center;

  .react-pdf__Page__canvas {
    margin: 0 auto;
    border-radius: 5px;
  }
`;

const BottomSlot = styled.div`
  position: absolute;
  left: 0;
  width: 100%;
`;

const BottomMeasureStub = styled.div`
  position: absolute;
  left: 0;
  width: 100%;
  height: 0;
  pointer-events: none;
`;

const EndSentinel = styled.div`
  position: absolute;
  left: 0;
  width: 100%;
  height: 1px;
  pointer-events: none;
`;
